import { supabase } from "@/integrations/supabase/client";
import { moderateText } from "@/api/moderation";
import { notifyContribution } from "@/api/notifyContribution";
import type { LngLat } from "@/utils/polyline";

export type TipCategory = "tip" | "warning" | "recommendation" | "info";

export interface TipPin {
  id: string;
  user_id: string;
  lat: number;
  lng: number;
  body: string;
  category: TipCategory;
  photo_pin_id: string | null;
  helpful_count: number;
  unhelpful_count: number;
  created_at: string;
  updated_at: string;
  /** Current user's vote on this tip, if any. Populated by loaders. */
  myVote?: 1 | -1 | 0;
}

interface CreateTipArgs {
  location: LngLat;
  body: string;
  category: TipCategory;
  photoPinId?: string;
}

/** Fetch all standalone tips (no photo_pin_id) plus the caller's own votes. */
export async function loadTipPins(): Promise<TipPin[]> {
  const [{ data, error }, votesRes] = await Promise.all([
    supabase
      .from("tip_pins")
      .select("*")
      .is("photo_pin_id", null)
      .order("helpful_count", { ascending: false })
      .limit(500),
    loadMyVotes(),
  ]);
  if (error) throw error;
  const myVotes = votesRes;
  return (data ?? []).map((t) => ({ ...(t as TipPin), myVote: myVotes.get(t.id) ?? 0 }));
}

/** Fetch comments attached to a specific photo pin (oldest first). */
export async function loadComments(photoPinId: string): Promise<TipPin[]> {
  const [{ data, error }, votes] = await Promise.all([
    supabase
      .from("tip_pins")
      .select("*")
      .eq("photo_pin_id", photoPinId)
      .order("created_at", { ascending: true })
      .limit(200),
    loadMyVotes(),
  ]);
  if (error) throw error;
  return (data ?? []).map((t) => ({ ...(t as TipPin), myVote: votes.get(t.id) ?? 0 }));
}

async function loadMyVotes(): Promise<Map<string, 1 | -1>> {
  const { data: u } = await supabase.auth.getUser();
  if (!u.user) return new Map();
  const { data } = await supabase
    .from("tip_votes")
    .select("tip_id, value")
    .eq("user_id", u.user.id);
  const m = new Map<string, 1 | -1>();
  for (const row of data ?? []) m.set(row.tip_id, row.value as 1 | -1);
  return m;
}

export async function createTip(args: CreateTipArgs): Promise<TipPin> {
  const { data: u, error: ue } = await supabase.auth.getUser();
  if (ue || !u.user) throw new Error("Sign in to leave a tip");
  const body = args.body.trim();
  if (body.length < 1 || body.length > 500) throw new Error("Tip must be 1–500 characters");

  // Run text through AI moderation. Throws with a friendly reason if blocked.
  await moderateText(body);

  const { data, error } = await supabase
    .from("tip_pins")
    .insert({
      user_id: u.user.id,
      lat: args.location[1],
      lng: args.location[0],
      body,
      category: args.category,
      photo_pin_id: args.photoPinId ?? null,
    })
    .select()
    .single();
  if (error) throw error;
  // Tell Morgan (July 31: "people are leaving reviews and probably tagging
  // roads and i'm not getting any emails"). Fire-and-forget — the tip is
  // already saved and a mail failure is not this person's problem.
  // R1: no coordinate is sent with the notification. A tip pin is a PLACE,
  // not a resolved road, so there is no road-derived point to send — and the
  // raw pin coordinate is exactly what must not leave the client. Place and
  // photo privacy is its own task; until it lands, the tip emails with no
  // location rather than with the contributor's.
  notifyContribution({
    kind: "tip",
    body,
    label: args.category ?? "",
    userId: u.user.id,
    source: "tip_pins",
  });
  return { ...(data as TipPin), myVote: 0 };
}

export async function deleteTip(id: string): Promise<void> {
  const { error } = await supabase.from("tip_pins").delete().eq("id", id);
  if (error) throw error;
}

/**
 * Cast / change / clear a vote. Pass 0 to remove an existing vote.
 * Returns the new client-side state {helpful_count, unhelpful_count, myVote}
 * computed locally so the UI can update before the trigger-driven counts
 * are re-fetched.
 */
export async function voteOnTip(
  tip: TipPin,
  next: 1 | -1 | 0,
): Promise<Pick<TipPin, "helpful_count" | "unhelpful_count" | "myVote">> {
  const { data: u, error: ue } = await supabase.auth.getUser();
  if (ue || !u.user) throw new Error("Sign in to vote");
  const userId = u.user.id;
  const prev = tip.myVote ?? 0;

  if (next === 0) {
    const { error } = await supabase
      .from("tip_votes")
      .delete()
      .eq("tip_id", tip.id)
      .eq("user_id", userId);
    if (error) throw error;
  } else if (prev === 0) {
    const { error } = await supabase
      .from("tip_votes")
      .insert({ tip_id: tip.id, user_id: userId, value: next });
    if (error) throw error;
  } else {
    const { error } = await supabase
      .from("tip_votes")
      .update({ value: next })
      .eq("tip_id", tip.id)
      .eq("user_id", userId);
    if (error) throw error;
  }

  // Optimistic local count adjustment.
  let helpful = tip.helpful_count;
  let unhelpful = tip.unhelpful_count;
  if (prev === 1) helpful = Math.max(0, helpful - 1);
  if (prev === -1) unhelpful = Math.max(0, unhelpful - 1);
  if (next === 1) helpful += 1;
  if (next === -1) unhelpful += 1;
  return { helpful_count: helpful, unhelpful_count: unhelpful, myVote: next };
}
